/**
 * Pure logic behind the trips board and the
 * trip detail page. Split out of the pages so day-grouping, filtering,
 * label lookups and the gating rules are testable without rendering — same
 * reasoning as `deliveriesBoard.ts` for isporuke.
 */
import type {
  CarrierView,
  DriverView,
  TripDirection,
  TripItemView,
  TripStatus,
  TripView,
  VehicleView,
  WeighingKind,
} from "../../api/transport";
import type { DeliveryStatus } from "../../api/deliveries";
import type { OrderStatus } from "../../api/orders";

export type TripFilter = {
  direction?: TripDirection | "";
  status?: TripStatus | "";
};

/**
 * Narrows an already-fetched list by direction/status. Day is deliberately a
 * server-side query param (`from`/`to` on the same date) rather than a client
 * filter here — the whole point of asking the server is to avoid pulling
 * every trip ever planned just to filter it away in the browser.
 */
export function filterTrips(trips: TripView[], filter: TripFilter): TripView[] {
  return trips.filter((t) => {
    if (filter.direction && t.direction !== filter.direction) return false;
    if (filter.status && t.status !== filter.status) return false;
    return true;
  });
}

export type TripDayGroup = {
  /** ISO date (yyyy-MM-dd), or null for a trip with no planned time yet. */
  day: string | null;
  trips: TripView[];
};

/**
 * The one zone in which "a day at the yard" is decided, matching `YardZone.java`
 * on the backend — the two must agree, or the board and the gate would disagree
 * about what "today" means at the same moment. Bankom has one yard, in Serbia.
 */
export const YARD_ZONE = "Europe/Belgrade";

const YARD_DAY_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: YARD_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * The calendar day an instant falls on at the yard, as `yyyy-MM-dd`.
 *
 * The backend stores `timestamptz` with `hibernate.jdbc.time_zone: UTC`, so every
 * `plannedAt` arrives with a `Z` offset — slicing the string would always answer
 * with the UTC day. A trip planned for 01:00 in Belgrade on 15 September comes
 * back as `2026-09-14T23:00:00Z`; parsing the instant and asking for its day in
 * `YARD_ZONE` files it under the 15th, where the yard expects it.
 */
export function yardDay(instant: string): string {
  return YARD_DAY_FORMAT.format(new Date(instant));
}

/** Today at the yard, as `yyyy-MM-dd` — the board's default day filter. */
export function yardToday(now: Date = new Date()): string {
  return YARD_DAY_FORMAT.format(now);
}

/**
 * Buckets trips by the calendar day `plannedAt` falls on at the yard (see
 * `yardDay`) — a trip planned for 23:30 Belgrade belongs to that day at the
 * yard, wherever the browser happens to be and whatever offset the value
 * arrives in. Day groups are sorted chronologically; trips with no planned time
 * land in a trailing `day: null` group instead of being dropped.
 */
export function groupTripsByDay(trips: TripView[]): TripDayGroup[] {
  const byDay = new Map<string, TripView[]>();
  const unscheduled: TripView[] = [];
  for (const t of trips) {
    if (!t.plannedAt) {
      unscheduled.push(t);
      continue;
    }
    const day = yardDay(t.plannedAt);
    const bucket = byDay.get(day);
    if (bucket) bucket.push(t);
    else byDay.set(day, [t]);
  }
  const groups: TripDayGroup[] = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, list]) => ({
      day,
      // By instant, not by string: two trips on the same yard day can arrive with
      // different offsets, and then lexicographic order is not chronological order.
      trips: [...list].sort(
        (a, b) => Date.parse(a.plannedAt as string) - Date.parse(b.plannedAt as string),
      ),
    }));
  if (unscheduled.length > 0) groups.push({ day: null, trips: unscheduled });
  return groups;
}

/** Vehicle plate for a trip's vehicle/trailer id, or null when unresolved
 * (deactivated, not yet loaded, or the trip simply has none). */
export function plateOf(vehicleId: string | null, vehicles: VehicleView[]): string | null {
  if (!vehicleId) return null;
  return vehicles.find((v) => v.id === vehicleId)?.plate ?? null;
}

/** Carrier name for a trip's carrier id, or null when unresolved. */
export function carrierNameOf(carrierId: string | null, carriers: CarrierView[]): string | null {
  if (!carrierId) return null;
  return carriers.find((c) => c.id === carrierId)?.name ?? null;
}

/** Driver full name for a trip's driver id, or null when unresolved. */
export function driverNameOf(driverId: string | null, drivers: DriverView[]): string | null {
  if (!driverId) return null;
  return drivers.find((d) => d.id === driverId)?.fullName ?? null;
}

/** Maps a trip status to one of the app's existing `.tag` palette classes — no
 * new component needed, this just picks a color. Shared by the board, the
 * detail page and the gate so none of the three disagree on what
 * PLANNED/ARRIVED/etc. look like. */
export function statusTagClass(status: TripStatus): string {
  switch (status) {
    case "PLANNED": return "info";
    case "ARRIVED": return "warn";
    case "DEPARTED": return "ok";
    case "CANCELLED": return "err";
  }
}

/** Maps a weighing kind to one of the app's existing `.tag` palette classes —
 * shared by the trip detail page (reads weighings) and the gate (records
 * them), so the two never disagree on what GROSS/TARE/CONTROL look like. */
export function weighingTagClass(kind: WeighingKind): string {
  switch (kind) {
    case "GROSS": return "info";
    case "TARE": return "neutral";
    case "CONTROL": return "warn";
  }
}

/**
 * Whether a trip in `status` can still be cancelled. Mirrors
 * `TripService#assertCancellable` exactly (PLANNED/ARRIVED only — a departed
 * or already-cancelled trip answers `INVALID_TRANSITION`), so the detail page
 * never offers a "Cancel trip" button the server would refuse.
 */
export function canCancelTrip(status: TripStatus): boolean {
  return status === "PLANNED" || status === "ARRIVED";
}

/**
 * Whether a trip in `status` may still have items added or removed. Mirrors
 * `TripService#assertItemsEditable` exactly (PLANNED/ARRIVED only — the server
 * answers `INVALID_TRANSITION` otherwise), so the detail page never offers an
 * "Add item" or "Remove" button on a departed or cancelled trip. Once the lorry
 * has left the yard, what it carried is history: editing it would record
 * something that never happened.
 *
 * Deliberately the same rule as `canCancelTrip` and deliberately a separate
 * function: the two answer different questions and are free to diverge if the
 * backend's ever do.
 */
export function canEditTripItems(status: TripStatus): boolean {
  return status === "PLANNED" || status === "ARRIVED";
}

/**
 * Whether the gate's "Arrived" button should be offered for a trip in
 * `status`. Mirrors `GateService#arrive`'s own transition check exactly
 * (`PLANNED` → `ARRIVED` only) so the gate screen never offers a button the
 * server would refuse with `INVALID_TRANSITION`.
 */
export function canArrive(status: TripStatus): boolean {
  return status === "PLANNED";
}

/**
 * Whether the gate's "Departed" button should be offered for a trip in
 * `status`. Mirrors `GateService#depart`'s own transition check exactly
 * (`ARRIVED` → `DEPARTED` only) — in particular, this must stay false for
 * `PLANNED`, so the button never appears before the vehicle has arrived.
 */
export function canDepart(status: TripStatus): boolean {
  return status === "ARRIVED";
}

/**
 * Whether recording a weighing of `kind` requires a reason. Mirrors
 * `GateService#weigh`'s own check exactly — only `CONTROL` demands one, and
 * the server otherwise answers `WEIGHING_REASON_REQUIRED` — so the gate form
 * never lets a control weighing through without one, and never demands a
 * reason for a plain GROSS/TARE reading where the server does not ask for it.
 */
export function isWeighingReasonRequired(kind: WeighingKind): boolean {
  return kind === "CONTROL";
}

/**
 * Whether a delivery in `status` is eligible to become an inbound trip item
 * Mirrors
 * `TripService#assertDeliveryStatusEligible` exactly, so the add-item picker
 * never offers a delivery the server would refuse with
 * `ITEM_STATUS_NOT_ELIGIBLE`. RECEIVED is included deliberately — it is the
 * same delivery a moment later, and excluding it would block recording a trip
 * after the fact for no safety benefit.
 */
export function isDeliveryEligibleForTrip(status: DeliveryStatus): boolean {
  return status === "CONFIRMED" || status === "RECEIVED";
}

/**
 * Whether an order in `status` is eligible to become an outbound trip item
 * Mirrors
 * `TripService#assertOrderStatusEligible` exactly. Excluding every
 * pre-approval status is what matters most here — the approval gate is a hard
 * rule in this project, and without this check a trip could carry an
 * unapproved order onto the yard regardless.
 */
export function isOrderEligibleForTrip(status: OrderStatus): boolean {
  return status === "APPROVED" || status === "SHIPPED";
}

/**
 * Which directions the "Plan a trip" button may offer, given the caller's
 * permissions. Mirrors `TripService`'s own check exactly — INBOUND needs
 * `trips:manage_inbound`, OUTBOUND needs `trips:manage_outbound` — so the UI
 * never presents a choice the server would refuse. The server remains the
 * authority; this only decides what to show. An empty result means the
 * button itself should not render.
 */
export function creatableTripDirections(hasPermission: (permission: string) => boolean): TripDirection[] {
  const directions: TripDirection[] = [];
  if (hasPermission("trips:manage_inbound")) directions.push("INBOUND");
  if (hasPermission("trips:manage_outbound")) directions.push("OUTBOUND");
  return directions;
}

/**
 * Which weighing the gate records first, given the trip's direction.
 *
 * An inbound lorry arrives loaded, so the first reading is GROSS and the tare
 * follows after unloading. An outbound one arrives empty: it is weighed as TARE
 * on the way in and GROSS once loaded. Net is gross minus tare either way — the
 * weighings are events and carry no ordering — but the gate form should open on
 * the reading the operator is about to take, not on the inbound one every time.
 */
export function firstWeighingKind(direction: TripDirection): WeighingKind {
  return direction === "INBOUND" ? "GROSS" : "TARE";
}

/**
 * The distinct counterparties a trip serves, in the order their first item
 * appears. Out-of-scope items contribute nothing — the viewer is not entitled
 * to know who they are, and counting them as a nameless extra would leak the
 * fact that another party is aboard.
 */
export function counterpartiesOf(items: TripItemView[]): string[] {
  const seen: string[] = [];
  for (const i of items) {
    if (!i.inScope || !i.counterpartyName) continue;
    if (!seen.includes(i.counterpartyName)) seen.push(i.counterpartyName);
  }
  return seen;
}

/**
 * Whether a trip carries goods for more than one counterparty.
 *
 * Drives the board badge and whether the item table groups at all: a trip for a
 * single customer must render exactly as it always has and pay nothing for a
 * feature it does not use.
 */
export function isMixedLoad(items: TripItemView[]): boolean {
  return counterpartiesOf(items).length > 1;
}

/**
 * The trip's total load, and how much of it is actually known.
 *
 * `quantityKg` is nullable — an item may be added before anyone knows its mass —
 * so a bare sum would render as a confident total that silently omits rows. The
 * caller gets the count of unstated items too, and must say so on screen rather
 * than print a number that looks complete.
 */
export function loadTotal(items: TripItemView[]): { kg: number; unstated: number } {
  let kg = 0;
  let unstated = 0;
  for (const i of items) {
    if (i.quantityKg == null) unstated += 1;
    else kg += i.quantityKg;
  }
  return { kg, unstated };
}
